/**
 * Shared DOM ↔ source-text helpers for the canvas inline text editor.
 *
 * Both `useCursor` and `useEventsCallbacks` walk the contentEditable's DOM
 * tree to map between (node, offset) selection anchors and source-text
 * character indices. The two must stay in lockstep — any divergence
 * silently misaligns the cursor from where the user typed. Keep them in one
 * file so they can't drift.
 */

/**
 * Convert a (container, offset) DOM selection position into a source-text
 * character index. Treats `<br>` as a single `\n` character, matching how
 * `innerText` reads the text back out.
 */
export const indexFromDomPosition = (
  root: Node,
  container: Node,
  offset: number
): number => {
  let result = 0
  let found = false
  const visit = (node: Node) => {
    if (found) return
    if (node === container) {
      if (container.nodeType === Node.TEXT_NODE) {
        result += offset
      } else {
        for (let i = 0; i < offset && i < node.childNodes.length; i++) {
          visit(node.childNodes[i])
          if (found) return
        }
      }
      found = true
      return
    }
    if (node.nodeType === Node.TEXT_NODE) {
      result += (node as Text).length
    } else if (node.nodeName === 'BR') {
      result += 1
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        visit(node.childNodes[i])
        if (found) return
      }
    }
  }
  visit(root)
  return result
}

/**
 * Reads the user-visible plain text out of a contentEditable element.
 * `innerText` preserves visual newlines from `<br>` and block boundaries;
 * `textContent` would lose them.
 *
 * Trailing-newline handling. Two competing requirements:
 *  - When the user presses Enter, the cursor must visibly move to a new
 *    line — so the engine needs a trailing newline in the source.
 *  - When the user backspaces back from an empty trailing line, the line
 *    should disappear — so the trailing newline must be stripped.
 *
 * The DOM alone can't tell these apart (Chrome leaves a ghost `<br>` either
 * way). We use the cursor position as the disambiguator: if the cursor sits
 * past all visible characters, the user is intentionally on the trailing
 * empty line — keep one newline. Otherwise the cursor has moved off, so
 * drop trailing newlines and let the engine shrink the bitmap.
 */
export const readEditorText = (el: HTMLElement): string => {
  // Normalize NBSPs that contentEditable inserts at line boundaries.
  const raw = el.innerText.replace(/\u00a0/g, ' ')
  if (/^\n+$/.test(raw)) return ''
  if (!raw.endsWith('\n')) return raw

  const visiblePart = raw.replace(/\n+$/, '')
  const sel = window.getSelection()
  if (sel && sel.focusNode && el.contains(sel.focusNode)) {
    const cursorIdx = indexFromDomPosition(el, sel.focusNode, sel.focusOffset)
    // Cursor past the last visible char → user is on the trailing empty
    // line. Preserve one newline so the engine renders that line for the
    // cursor. Collapses Chrome's ghost double-`<br>` to a single `\n`.
    if (cursorIdx > visiblePart.length) {
      return visiblePart + '\n'
    }
  }
  // Cursor is on (or before) the visible content — drop ALL trailing
  // newlines so the bitmap doesn't keep dead empty lines after a backspace.
  return visiblePart
}
